import {
    Box,
    Drawer,
    Typography,
    IconButton,
    Fade,
    Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useEffect, useState } from "react";
import { ExamCode } from "../../../../api/interfaces/preboard/ExamCode";
import { getExamsByExamCode } from "../../../../api/services/preboard/examinee";
import { handleApiError } from "../../../../utils/errorHandler";
import { pushNotification } from "../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { ModifyAttemptView } from "./view/ModifyAttemptView/ModifyAttemptView";
import { ExamListView } from "./view/ExamListView/ExamListView";
import { ExamResponse } from "../../../../api/interfaces/preboard/Exam";

type ExamineeExamDrawerProps = {
    examineeId: string;
    examineeFullName: string;
    activatedExamCodes: ExamCode[];
    isDrawerOpen: boolean;
    onAttemptDeleted?: () => void;
    onClose: () => void;
};

const ExamineeExamDrawer: React.FC<ExamineeExamDrawerProps> = (props) => {
    const { examineeId, examineeFullName, activatedExamCodes, isDrawerOpen, onAttemptDeleted, onClose } = props;

    // Recount
    const [drawerAttemptTick, setDrawerAttemptTick] = useState(0);

    const [expandedExamId, setExpandedExamId] = useState<string | null>(null);
    const [loadingByCode, setLoadingByCode] = useState<Record<string, boolean>>({});
    const [examsByCode, setExamsByCode] = useState<Record<string, any[]>>({});

    // Drawer content switching
    const [view, setView] = useState<"LIST" | "MODIFY">("LIST");
    const [selectedExam, setSelectedExam] = useState<ExamResponse | null>(null);

    const handleCloseDrawer = () => {
        setExpandedExamId(null);
        setExamsByCode({});
        setLoadingByCode({});
        setView("LIST")
        setSelectedExam(null);
        onClose();
    };

    // LIST
    const handleToggle = async (codeId: string) => {
        setExpandedExamId(null);
        if (expandedExamId === codeId) {
            setExpandedExamId(null);
            return;
        }

        setExpandedExamId(codeId);

        // Already fetched → do nothing
        if (examsByCode[codeId]) return;

        setLoadingByCode((prev) => ({ ...prev, [codeId]: true }));

        try {
            const examsByExamCode = await getExamsByExamCode(examineeId, codeId);
            setExamsByCode((prev) => ({
                ...prev,
                [codeId]: examsByExamCode.data.exams,
            }));
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }

        setLoadingByCode((prev) => ({ ...prev, [codeId]: false }));
    };

    const handleBack = () => {
        setView("LIST");
        setSelectedExam(null);
    };

    // MODIFY
    const handleModify = (exam: ExamResponse) => {
        setSelectedExam(exam);
        setView("MODIFY");
    }

    
    const handleAttemptDeletedInternal = () => {
        setDrawerAttemptTick((prev) => prev + 1);
        onAttemptDeleted?.();
    };

    // Recount of attempts
    useEffect(() => {
        if (drawerAttemptTick === 0) return;

        setExamsByCode((prev) => {
            const updated = { ...prev };

            Object.keys(updated).forEach((codeId) => {
                updated[codeId] = updated[codeId].map((exam) =>
                    exam._id === selectedExam?._id
                        ? {
                            ...exam,
                            numberOfAttempts: Math.max(
                                0,
                                exam.numberOfAttempts - 1
                            ),
                        }
                        : exam
                );
            });

            return updated;
        });
    }, [drawerAttemptTick, selectedExam]);

    return (
        <Drawer
            anchor="right"
            open={isDrawerOpen}
            onClose={handleCloseDrawer}
            slotProps={{
                paper: {
                    sx: {
                        width: {
                            xs: "100%",
                            sm: 700,
                        },
                        display: "flex",
                        flexDirection: "column",
                    },
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    mx: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {view === "MODIFY" && (
                        <IconButton onClick={handleBack}>
                            <ArrowBackOutlinedIcon />
                        </IconButton>
                    )}

                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {view === "LIST"
                            ? `${examineeFullName} List of Exams`
                            : `${examineeFullName} Modify Attempt`}
                    </Typography>
                </Box>

                <IconButton onClick={handleCloseDrawer}>
                    <CloseIcon />
                </IconButton>
            </Box>


            {/* Body */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    p: 3,
                    overflowY: "auto",
                }}
            >

                {view === "LIST" && (
                    <Fade in={view === "LIST"} timeout={250} unmountOnExit>
                        <Box>
                            <ExamListView
                                examineeId={examineeId}
                                activatedExamCodes={activatedExamCodes}
                                expandedExamId={expandedExamId}
                                loadingByCode={loadingByCode}
                                examsByCode={examsByCode}
                                onToggle={handleToggle}
                                onModify={handleModify}
                            />
                        </Box>
                    </Fade>
                )}

                {view === "MODIFY" && selectedExam && (
                    <Slide
                        direction="left"
                        in={view === "MODIFY"}
                        timeout={300}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Box>
                            {selectedExam && (
                                <ModifyAttemptView
                                    exam={selectedExam}
                                    onAttemptDeleted={handleAttemptDeletedInternal}
                                />
                            )}
                        </Box>
                    </Slide>
                )}
            </Box>
        </Drawer>
    );
};

export default ExamineeExamDrawer;
